
export interface BenefitGroup {
    label: string;
    note?: string;
    items: string[];
    singleColumn?: boolean;
    ordered?: boolean;      // renderiza como <ol> numerada
    alphabetical?: boolean; // renderiza como sub-lista <ol type="a"> indentada, sem caixa
}

export interface ServiceData {
    id: string;
    slug: string;
    hero: {
        title: string;
        subtitle: string;
        heroTitle?: string; // título completo para o h1 (sobrescreve title+subtitle no hero visual)
        breadcrumbLabel?: string; // label do breadcrumb (sobrescreve subtitle quando definido)
        description: string;
        image: string;
        hideTitle?: boolean;
    };
    seo: {
        title: string;
        description: string;
        keywords: string;
    };
    overview: {
        title: string;
        content: string[]; // Parágrafos explicativos
    };
    specializedAreas: {
        title: string;
        items: {
            id: string; // Para âncoras de navegação
            icon: string;
            title: string;
            description: string; // Versão curta para cards se necessário
            image?: string; // Imagem destacada opcional para a seção
            detailedDescription: string[]; // Explicações técnicas robustas (2 a 3 parágrafos)
            benefits?: string[]; // Listas de benefícios técnicos (flat)
            benefitGroups?: BenefitGroup[]; // Grupos hierárquicos de benefícios/documentos
            benefitsLabel?: string; // Label customizado para a lista de benefícios
            postListDescription?: string[]; // Parágrafos exibidos após as listas de documentos/benefícios
            faq?: { question: string; answer: string }[]; // Dúvidas frequentes da área
        }[];
    };
    cta?: {
        text: string;
        buttonLabel: string;
        href: string;
    };
    successCases: {
        title: string;
        items: {
            tag: string;
            title: string;
            description: string;
            image: string;
        }[];
    };
}


export const servicesData: Record<string, ServiceData> = {
    "imobiliario": {
        id: "01",
        slug: "imobiliario",
        hero: {
            title: "Regularização",
            subtitle: "Imobiliária",
            description: "Usucapião Extrajudicial e Judicial, Inventário ou divórcio com partilha de bens.",
            image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&q=80"
        },
        seo: {
            title: "Usucapião Extrajudicial e Regularização Imobiliária | Pacces Advogados",
            description: "Especialistas em Usucapião Extrajudicial e Judicial em São Paulo. Regularize seu imóvel com segurança jurídica e agilidade. Consulte nossos especialistas.",
            keywords: "usucapião extrajudicial são paulo, regularização imobiliária, contrato de gaveta, usucapião judicial, advogado imobiliário são paulo"
        },
        overview: {
            title: "",
            content: [
                "A regularização imobiliária abrange um conjunto de procedimentos jurídicos destinados a formalizar e assegurar a titularidade de imóveis."
            ]
        },
        specializedAreas: {
            title: "",
            items: [
                {
                    id: "usucapiao",
                    icon: "home",
                    title: "Usucapião Extrajudicial e Judicial",
                    description: "Regularização imobiliária pela via extrajudicial (cartório) ou judicial.",
                    image: "https://images.unsplash.com/photo-1582407947304-fd86f028f716?auto=format&fit=crop&q=80",
                    detailedDescription: [
                        'O Usucapião Extrajudicial se trata de um procedimento simplificado destinado à regularização imobiliária, em regra, se aplicará em hipóteses de imóveis irregulares, em que o proprietário possuí somente um instrumento particular, popularmente conhecido como "contrato de gaveta", ou, as vezes, não possuí nem este contrato, tendo, contudo, atuado como dono por longo período de tempo (05, 10 ou 15 anos) pagando impostos, contas de consumo e mantendo o imóvel.',
                        "O procedimento de Usucapião Extrajudicial leva em torno de 06 (seis) meses para apartamento e 01 (um) ano para residências, podendo ser solucionado, às vezes, em prazos inferiores.",
                        "Primeiramente, em muitos casos, é realizado um levantamento por um Engenheiro parceiro, que elaborará uma Planta topográfica, Memorial Descritivo e ART, após, será enviado ao Tabelião de Notas para elaboração da Ata Notarial, documento que servirá para atestar a posse sobre o imóvel.",
                        "Na sequência, o escritório montará o processo e emitirá todas as certidões pertinentes para prosseguir com o protocolo perante o Registro de Imóveis competente, onde se processará o pedido."
                    ],
                    benefitGroups: [
                        {
                            label: "Veja os documentos necessários para requerer o Usucapião",
                            ordered: true,
                            singleColumn: true,
                            items: [
                                "Documentos pessoais dos requerentes (RG, CPF ou CNH)",
                                "Certidão de Nascimento e / ou Casamento",
                                "Comprovante de Residência atualizado",
                                "Documentos do imóvel (Matrícula / IPTU e Contratos)",
                                "Documentos que comprovam a posse sobre o imóvel, tais como (não há necessidade de obter todos):"
                            ]
                        },
                        {
                            label: "",
                            alphabetical: true,
                            singleColumn: true,
                            items: [
                                "Contas de consumo (água, luz, telefonia)",
                                "Pagamentos de Impostos (IPTU)",
                                "Comprovantes de melhorias realizadas no imóvel (reformas)",
                                "Contrato particular, se houver (\"contrato de gaveta\")",
                                "Imposto de Renda comprovando a declaração do imóvel",
                                "Correspondências recebidas no local do imóvel em nome dos requerentes",
                                "Contratos de aluguel, caso o imóvel seja alugado"
                            ]
                        }
                    ],
                    faq: [
                        {
                            question: "Preciso morar no imóvel para requerer a Usucapião?",
                            answer: "Não! Muitas pessoas acreditam que a Usucapião só se aplica a imóveis em que residam, mas não é verdade, a Usucapião poderá ser requerida em (i) imóveis desocupados, desde que comprovada a manutenção deste imóvel e pagamento de tributos; (ii) imóveis alugados; (iii) imóveis decorrentes de herança (até mesmo em inventário)."
                        },
                        {
                            question: "Preciso fazer inventário para requerer a Usucapião?",
                            answer: "Não! É possível ingressar com a Usucapião apenas com a comprovação da continuidade da posse que era exercida pelos pais passando para os filhos / herdeiros. O procedimento demandará uma comprovação de soma de posse, algo simples de se atestar."
                        },
                        {
                            question: "O imóvel foi recebido por herança, ele pertencerá ao meu cônjuge também?",
                            answer: "É preciso verificar o regime de casamento, contudo, o cônjuge poderá declarar que não possuí interesse no imóvel e a Usucapião não lhe passará a propriedade. Assim como, caso queira receber o imóvel também, poderá ser feito."
                        },
                        {
                            question: "Qual a vantagem do Usucapião Judicial? E sua desvantagem?",
                            answer: "Muitas vezes, em hipóteses de baixa renda, é recomendado o Usucapião Judicial para requerimento de isenção de diversas taxas, através da Justiça Gratuita, garantindo o acesso até as famílias com menor renda, à regularização do seu imóvel! A desvantagem é que este procedimento levará mais tempo do que o Extrajudicial."
                        },
                        {
                            question: "Qual a vantagem do Usucapião Extrajudicial? E sua desvantagem?",
                            answer: "A vantagem é que se trata de um procedimento mais rápido que o Judicial, contudo, por se tratar de procedimento que corre diretamente em cartórios, todos os pagamentos são à vista e de valores mais elevados."
                        },
                        {
                            question: "Comprei um imóvel irregular há pouco tempo, posso requerer a Usucapião?",
                            answer: "Sim! Claro! Esta é a vantagem da Usucapião! Você poderá requerer a soma de posse com o proprietário anterior, então se o antigo proprietário já detinha posse por um longo período, você poderá requerer que essa posse seja acrescida à sua, resultando no cumprimento dos requisitos para o deferimento da Usucapião."
                        }
                    ]
                },
                {
                    id: "inventario",
                    icon: "family_restroom",
                    title: "Inventário Extrajudicial e Judicial",
                    description: "Regularização da transmissão de bens pelo procedimento extrajudicial (cartório) ou judicial.",
                    image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&q=80",
                    detailedDescription: [
                        "O Inventário com partilha se trata de procedimento adequado para regularizar a transmissão de bens do \"de cujus\" (falecido) ao cônjuge sobrevivente e seus herdeiros, ele poderá ser realizado de forma extrajudicial, descomplicada, diretamente em cartório, ou, de forma judicial, um pouco mais burocrática, mas efetiva."
                    ],
                    postListDescription: [
                        "O Inventário Extrajudicial, para ser realizado, deverá observar alguns requisitos, como: (i) todas as partes devem ser maiores e capazes; (ii) todas as partes devem ser concordes entre sí, principalmente no tocante à divisão dos bens; (iii) inexistência de testamento.",
                        "No caso de existirem menores, há a possibilidade, recente, de se processar o inventário de forma extrajudicial, desde que haja a manifestação do Ministério Público no procedimento. Assim como, caso exista testamento, é possível que se abra e se cumpra o testamento de forma judicial, para, posteriormente, partir para o inventário extrajudicial em cartório.",
                        "O Inventário Judicial, por sua vez, é utilizado, muitas vezes, quando há discordância entre a divisão de bens pelos herdeiros, ou, até mesmo, com a existência de menores / incapazes, o que acaba deixando o trâmite mais moroso, demandando uma atuação mais ativa do advogado perante o Juiz responsável.",
                        "Na esfera judicial é possível requerer o deferimento da justiça gratuita para os requerentes, ou o recolhimento diferenciado de custas, o que, por muitas vezes, é vantajoso aos herdeiros, justificando o procedimento."
                    ],
                    benefitGroups: [
                        {
                            label: "Para realização do inventário, serão necessários os seguintes documentos preliminares",
                            singleColumn: true,
                            items: [
                                "Documentos pessoais do \"de cujus\" (falecido), do cônjuge sobrevivente e dos herdeiros e seus cônjuges, como RG, CPF ou CNH",
                                "Certidão de Nascimento e / ou Casamento do \"de cujus\" e dos herdeiros",
                                "Comprovante de Residência atualizado do cônjuge sobrevivente e dos herdeiros"
                            ]
                        },
                        {
                            label: "Documentos dos bens a serem inventariados",
                            singleColumn: true,
                            items: [
                                "Imóvel (Matrícula, escritura e IPTU)",
                                "Automóvel (CRLV)",
                                "Investimentos / Contas Bancárias (extratos atualizados)",
                                "Outros (a verificar)"
                            ]
                        }
                    ],
                    faq: [
                        {
                            question: "Qual o prazo do inventário extrajudicial?",
                            answer: "Normalmente o prazo estimado é de 20 (vinte) a 40 (quarenta) dias, a depender da quantidade de herdeiros, bens, pagamento à vista do imposto (ITCMD) e questões particulares de cada caso concreto."
                        },
                        {
                            question: "Qual o valor do Imposto (ITCMD)? É possível ter algum desconto?",
                            answer: "A alíquota do ITCMD para o Estado de São Paulo é de 4% sobre o valor a ser partilhado. Caso o imposto seja recolhido em até 90 (noventa) dias do falecimento, haverá desconto de 5%."
                        }
                    ]
                },
                {
                    id: "divorcio-extrajudicial",
                    icon: "handshake",
                    title: "Divórcio Extrajudicial",
                    description: "Dissolução do casamento e divisão de bens diretamente em cartório.",
                    image: "https://images.unsplash.com/photo-1603796846097-bee99e4a601f?auto=format&fit=crop&q=80",
                    detailedDescription: [
                        "O Divórcio Extrajudicial com partilha se trata de procedimento adequado para dissolver o casamento e regularizar a divisão dos bens do casal, ele poderá ser realizado de forma extrajudicial, descomplicada, diretamente em cartório, em casos que não existam filhos menores.",
                        "Requisitos: Haver consenso entre os divorciandos, principalmente com relação aos termos de divisão de bens e eventual pensão.",
                        "O prazo para a concretização do divórcio extrajudicial leva em torno de 20 (vinte) a 40 (quarenta) dias, a depender do caso concreto e disponibilidade do Tabelião responsável."
                    ],
                    benefitsLabel: "Condições e Destaques",
                    benefits: [
                        "Procedimento em cartório — sem necessidade de ação judicial",
                        "Aplicável quando não há filhos menores no vínculo conjugal",
                        "Consenso entre os divorciandos quanto à divisão dos bens",
                        "Prazo estimado de 20 (vinte) a 40 (quarenta) dias",
                        "Maior privacidade e celeridade em relação ao divórcio judicial"
                    ]
                }
            ]
        },
        cta: {
            text: "Faça uma reunião com um de nossos especialistas e regularize já o seu imóvel!",
            buttonLabel: "Agendar Consulta",
            href: "https://wa.me/5511969241378?text=Olá,%20gostaria%20de%20agendar%20uma%20consulta%20sobre%20Regularização%20Imobiliária"
        },
        successCases: {
            title: "Resultados de Alta Complexidade",
            items: []
        }
    },
    "bancario": {
        id: "03",
        slug: "bancario",
        hero: {
            title: "Consultoria Estratégica",
            subtitle: "Produtos Bancários",
            heroTitle: "Consultoria Estratégica em Produtos Bancários",
            description: "O escritório fornece consultoria com foco em Crédito Consignado, Home Equity e Middle Market, desde a estruturação até a execução, mitigando riscos e prevenindo passivos futuros.",
            image: "https://images.unsplash.com/photo-1501167786227-4cba60f6d58f?auto=format&fit=crop&q=80"
        },
        seo: {
            title: "Consultoria em Produtos Bancários | Pacces Advogados",
            description: "Especialistas em Home Equity (CGI), Crédito Consignado e Middle Market. Consultoria bancária estratégica em São Paulo.",
            keywords: "home equity, crédito consignado, middle market, consultoria bancária, due diligence imobiliária, CGI"
        },
        overview: {
            title: "",
            content: []
        },
        specializedAreas: {
            title: "Frentes de Atuação",
            items: [
                {
                    id: "home-equity",
                    icon: "home",
                    title: "Home Equity (CGI)",
                    description: "Crédito com garantia imobiliária, da estruturação ao registro.",
                    image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&q=80",
                    detailedDescription: [
                        "O produto Home Equity (CGI), ou crédito com garantia imobiliária, é uma das principais frentes de atuação do escritório, que executa as seguintes etapas:"
                    ],
                    benefits: [
                        "Instrumentalização: Elaboração de instrumentos jurídicos para viabilização da operação",
                        "Due Diligence Imobiliária: Elaboração de Due Diligence Imobiliária aprofundada, através da emissão de certidões e documentos do imóvel ofertado em garantia, proprietários, empresas e eventuais antecessores, incluindo, ainda, análise processual detalhada e indicação de riscos",
                        "Formalização: Preenchimento e adequação dos instrumentos jurídicos para concretização da operação",
                        "Registro de Garantias: Registro das garantias perante os cartórios de registro de imóveis competentes",
                        "Consolidação de propriedade: Em casos de inadimplência, providenciamos o procedimento completo para a consolidação da propriedade do imóvel que garante a operação"
                    ]
                },
                {
                    id: "credito-consignado",
                    icon: "payments",
                    title: "Crédito Consignado",
                    description: "Cessão de carteiras, contencioso cível e cobrança especializada.",
                    image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&q=80",
                    detailedDescription: [
                        "O Crédito Consignado ganhou grande destaque nos últimos anos no mercado bancário, sendo outra frente de especialidade do Pacces Advogados, desde a cessão de carteiras de crédito consignado, até a atuação contenciosa cível e cobrança."
                    ]
                }
            ]
        },
        cta: {
            text: "Conheça mais sobre a nossa assessoria e atuação em produtos bancários, entre em contato.",
            buttonLabel: "Entre em Contato",
            href: "https://wa.me/5511969241378?text=Olá,%20gostaria%20de%20saber%20mais%20sobre%20consultoria%20em%20produtos%20bancários"
        },
        successCases: {
            title: "Resultados em Produtos Bancários",
            items: []
        }
    },
    "civel": {
        id: "06",
        slug: "civel",
        hero: {
            title: "Contencioso Cível",
            subtitle: "Massificado",
            breadcrumbLabel: "Cível Massificado",
            description: "CDC, Crédito Consignado, demandas ativas e contrárias.",
            image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&q=80"
        },
        seo: {
            title: "Contencioso Cível Massificado | Pacces Advogados",
            description: "Mais de 2.000 ações patrocinadas em CDC e Crédito Consignado. Alto índice de êxito em demandas ativas e contrárias. Contencioso massificado com alta performance técnica.",
            keywords: "contencioso cível massificado, CDC financiamento veículos, crédito consignado, advogado bancário são paulo, ações massificadas"
        },
        overview: {
            title: "",
            content: [
                "O escritório detém ampla atuação em carteiras de contencioso cível massificado, com foco em produtos bancários, como CDC (Financiamento de veículos) e Crédito Consignado, com mais de 2.000 ações patrocinadas.",
                "O foco do escritório é a atuação técnica precisa e rápida, visando manter o mínimo de processos em carteira, mitigando prejuízos e prevenindo novos ajuizamentos.",
                "A equipe técnica do Pacces Advogados é amparada pelos melhores procedimentos e tecnologias para este tipo de produto, obtendo resultados consolidados, com alto índice de êxito nas demandas."
            ]
        },
        specializedAreas: {
            title: "Frentes de Atuação",
            items: []
        },
        cta: {
            text: "Conheça mais sobre a nossa assessoria em Contencioso Cível Massificado, entre em contato.",
            buttonLabel: "Entre em Contato",
            href: "https://wa.me/5511969241378?text=Olá,%20gostaria%20de%20saber%20mais%20sobre%20Contencioso%20Cível%20Massificado"
        },
        successCases: {
            title: "Vereditos e Acordos Estratégicos",
            items: []
        }
    },
    "civel-estrategico": {
        id: "07",
        slug: "civel-estrategico",
        hero: {
            title: "Contencioso Cível",
            subtitle: "Estratégico",
            breadcrumbLabel: "Cível Estratégico",
            description: "Casos de alta complexidade do âmbito cível.",
            image: "https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&q=80"
        },
        seo: {
            title: "Contencioso Cível Estratégico | Pacces Advogados",
            description: "Assessoria jurídica personalizada em demandas cíveis de alta complexidade. Direito contratual, imobiliário e família e sucessões em São Paulo.",
            keywords: "contencioso cível estratégico, direito contratual, demandas de alta complexidade, advogado cível são paulo"
        },
        overview: {
            title: "",
            content: [
                "Assessoria jurídica no patrocínio de demandas de alta complexidade, através de solução personalizada para cada caso concreto, com foco no resultado esperado pelo cliente.",
                "Atuação em demandas envolvendo direito contratual, imobiliário e família e sucessões, com alto valor envolvido."
            ]
        },
        specializedAreas: {
            title: "",
            items: []
        },
        cta: {
            text: "Conheça mais sobre a nossa assessoria personalizada em Contencioso Cível, agende uma consulta com nosso especialista.",
            buttonLabel: "Agendar Consulta",
            href: "https://wa.me/5511969241378?text=Olá,%20gostaria%20de%20agendar%20uma%20consulta%20sobre%20Contencioso%20Cível%20Estratégico"
        },
        successCases: {
            title: "Casos de Alta Complexidade",
            items: []
        }
    },
    "operacoes-imobiliarias": {
        id: "08",
        slug: "operacoes-imobiliarias",
        hero: {
            title: "Operações",
            subtitle: "Imobiliárias",
            description: "Assessoria técnica completa em compra e venda de imóveis, do início ao fim da operação, com segurança jurídica e tranquilidade.",
            image: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&q=80"
        },
        seo: {
            title: "Assessoria em Compra e Venda de Imóveis | Pacces Advogados",
            description: "Assessoria jurídica completa em compra e venda de imóveis em São Paulo. Due Diligence, escritura pública e acompanhamento do início ao registro.",
            keywords: "compra e venda imóvel, assessoria imobiliária, due diligence imobiliária, escritura pública, advogado imobiliário são paulo"
        },
        overview: {
            title: "",
            content: []
        },
        specializedAreas: {
            title: "Como Funciona a Assessoria",
            items: [
                {
                    id: "compra-e-venda",
                    icon: "real_estate_agent",
                    title: "Compra e Venda de Imóveis",
                    description: "Assessoria completa em transações imobiliárias, da Due Diligence ao registro.",
                    image: "/compra-venda-imoveis.jpg",
                    detailedDescription: [
                        "O Pacces Advogados auxilia seus clientes durante toda a jornada de compra e venda de imóveis, com a assessoria técnica completa, do início ao fim da aquisição, garantindo a maior segurança e tranquilidade.",
                        "No início da jornada, o cliente indicará ao escritório o imóvel escolhido e que deseja fechar negócio, e, a partir deste ponto, o escritório inicia a Due Diligence Imobiliária dos documentos e certidões do imóvel, dos seus proprietários, de eventuais pessoas jurídicas que os proprietários sejam titulares e, caso haja, de eventuais antecessores.",
                        "Com a etapa de Due Diligence aprovada, o escritório seguirá para a elaboração do compromisso de compra e venda, a fim de personalizar as cláusulas necessárias para a negociação do caso concreto, e, caso a negociação e contrato estejam sendo elaborados por um corretor de imóveis, o escritório poderá assessorar os clientes e analisar / alterar o contrato que será feito pelo corretor.",
                        "No ato seguinte, o escritório acompanhará o cumprimento do compromisso e conduzirá a lavratura da escritura pública de compra e venda ou, caso seja aplicável, a assinatura do contrato de financiamento bancário.",
                        "E, por fim, acompanhará o registro do imóvel em nome dos novos proprietários e regularização perante os cadastros da Prefeitura local."
                    ],
                },
                {
                    id: "doacao-imoveis",
                    icon: "volunteer_activism",
                    title: "Doação de Imóveis",
                    description: "Transferência de patrimônio imobiliário em vida com segurança jurídica e planejamento tributário.",
                    detailedDescription: [
                        "A doação de imóveis é uma das formas mais seguras e eficazes de organizar a transferência de patrimônio ainda em vida, mas exige formalidades rigorosas para evitar nulidades. No Brasil, para que a doação tenha validade jurídica, é indispensável a lavratura de uma escritura pública em cartório (para imóveis acima de 30 salários mínimos), seguida do devido registro na matrícula do bem. Além disso, é necessário planejar o impacto do ITCMD (Imposto sobre Transmissão Causa Mortis e Doação), um tributo estadual que incide sobre a operação. Atuar de forma preventiva e extrajudicial permite que o cálculo desse imposto seja otimizado e que o processo ocorra com agilidade, garantindo que a propriedade seja transferida sem as burocracias e os custos elevados de um inventário futuro.",
                        "Quando o objetivo é a doação de pais para filhos, a atenção deve ser redobrada, pois a lei entende esse ato como uma antecipação de legítima (adiantamento da herança). Para garantir a paz familiar e a segurança do negócio, é essencial respeitar os limites da parte disponível do patrimônio, evitando que a doação prejudique outros herdeiros necessários. Uma estratégia jurídica inteligente é a inclusão da cláusula de usufruto vitalício, que permite aos pais continuarem utilizando ou recebendo aluguéis do imóvel enquanto viverem. Também podem ser aplicadas cláusulas de incomunicabilidade e impenhorabilidade, que protegem o bem contra dívidas ou eventuais divórcios dos filhos, assegurando que o patrimônio permaneça blindado e preservado dentro da linhagem familiar."
                    ]
                }
            ]
        },
        cta: {
            text: "Conheça mais sobre a nossa assessoria personalizada em Compra e Venda de Imóveis, agende uma consulta com nosso especialista.",
            buttonLabel: "Agendar Consulta",
            href: "https://wa.me/5511969241378?text=Olá,%20gostaria%20de%20agendar%20uma%20consulta%20sobre%20Compra%20e%20Venda%20de%20Imóveis"
        },
        successCases: {
            title: "Operações Imobiliárias Concluídas",
            items: []
        }
    },
};
